import { Helmet } from '@dr.pogodin/react-helmet';

interface OrganizationSchemaProps {
  type: 'organization';
}

interface SoftwareApplicationSchemaProps {
  type: 'softwareApplication';
  name: string;
  price: string;
  ratingValue?: string;
  reviewCount?: string;
}

interface FAQSchemaProps {
  type: 'faq';
  questions: Array<{
    question: string;
    answer: string;
  }>;
}

interface ProductSchemaProps {
  type: 'product';
  name: string;
  price: string;
  currency?: string;
  description?: string;
}

interface ArticleSchemaProps {
  type: 'article';
  headline: string;
  author: string;
  datePublished: string;
  dateModified?: string;
  image?: string;
}

interface BreadcrumbSchemaProps {
  type: 'breadcrumb';
  items: Array<{
    name: string;
    url: string;
  }>;
}

type SchemaProps =
  | OrganizationSchemaProps
  | SoftwareApplicationSchemaProps
  | FAQSchemaProps
  | ProductSchemaProps
  | ArticleSchemaProps
  | BreadcrumbSchemaProps;

export default function SchemaMarkup(props: SchemaProps) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://axetra.io';

  const getSchema = () => {
    switch (props.type) {
      case 'organization':
        return {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Axetra',
          url: siteUrl,
          logo: `${siteUrl}/images/logo.png`,
          description: 'Advanced traffic filtering and bot detection platform for digital advertisers',
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'Customer Support',
            email: 'support@axetra.io',
            availableLanguage: ['English', 'Russian', 'German', 'Spanish'],
          },
          sameAs: [
            'https://twitter.com/axetra',
            'https://linkedin.com/company/axetra',
            'https://facebook.com/axetra',
          ],
        };

      case 'softwareApplication':
        return {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: props.name,
          applicationCategory: 'SecurityApplication',
          operatingSystem: 'Web',
          offers: {
            '@type': 'Offer',
            price: props.price,
            priceCurrency: 'USD',
            availability: 'https://schema.org/InStock',
          },
          ...(props.ratingValue && props.reviewCount
            ? {
                aggregateRating: {
                  '@type': 'AggregateRating',
                  ratingValue: props.ratingValue,
                  reviewCount: props.reviewCount,
                },
              }
            : {}),
        };

      case 'faq':
        return {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: props.questions.map((q) => ({
            '@type': 'Question',
            name: q.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: q.answer,
            },
          })),
        };

      case 'product':
        return {
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: props.name,
          description: props.description || `${props.name} - Axetra traffic filtering platform`,
          offers: {
            '@type': 'Offer',
            price: props.price,
            priceCurrency: props.currency || 'USD',
            availability: 'https://schema.org/InStock',
            seller: {
              '@type': 'Organization',
              name: 'Axetra',
            },
          },
        };

      case 'article':
        return {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: props.headline,
          author: {
            '@type': 'Organization',
            name: props.author,
          },
          publisher: {
            '@type': 'Organization',
            name: 'Axetra',
            logo: {
              '@type': 'ImageObject',
              url: `${siteUrl}/images/logo.png`,
            },
          },
          datePublished: props.datePublished,
          dateModified: props.dateModified || props.datePublished,
          ...(props.image ? { image: props.image } : {}),
        };

      case 'breadcrumb':
        return {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: props.items.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: `${siteUrl}${item.url}`,
          })),
        };

      default:
        return null;
    }
  };

  const schema = getSchema();

  if (!schema) return null;

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}
